import type { CSSProperties } from 'react'
import { useCopy } from '../../content'
import { BASE } from '../../data/comuni'
import features from '../../data/mapFeatures.json'
import { LANDMARKS } from '../../data/mapLandmarks'
import { VC_COMUNI } from '../../data/servedComuni'
import { srcFor, srcSetFor } from '../media/srcset'
import { layoutLabels, textWidth, type Box, type LayoutOptions, type PlacedLabel } from './labelLayout'
import { FRAME, compactPath, frameKmPerUnit, frameProject, round2 } from './mapProjection'

/**
 * Territory map of the Vercellese (owner 2026-09-28: every comune served on
 * the map, no route «spider», the page about Vercelli only; the Valsesia is
 * the partner Verde Valsesia's), on the fixed frame of `data/mapFrame.json`:
 * the provincia di Vercelli from just above Lozzolo and Gattinara down to the
 * Po, 800 × 869 units, 1 unit ≈ 66 m. Novara and Casale Monferrato are served
 * (the Zone table lists them) but sit outside the frame.
 *
 * Two layers share one box sized by the viewBox aspect:
 * - the painted-territory raster (`scripts/media/render-territory.mjs`): the
 *   rice-paddy mosaic field by field, the woods, the canals and the hills at
 *   the edges — in full colour inside the province, washed pale outside it,
 *   its border dashed. A light and a dark `<picture>` switched by the site's
 *   `dark` class — both lazy, the hidden one `display: none`;
 * - the SVG drawing on top, in the same user space: rivers and roads
 *   (`data/mapFeatures.json`, OpenStreetMap), the territory's names
 *   (`data/mapLandmarks.ts`), every served comune as a dot, and their names.
 *
 * Names: they do not all fit at a readable size, so `labelLayout.ts` places
 * them greedily — the base first, then the main towns, then by distance from
 * the base — wherever one fits clear of the others; the rest keep their name
 * in the dot's `<title>` (and in the table). The drawing scales with its box,
 * so a readable size in user units depends on the width it is shown at: one
 * set of names for desktop (`max-md:hidden`) and a larger, sparser one for
 * phones (`md:hidden`), each computed for the page's variant. Landmark and
 * river names show from md only.
 *
 * Legend, scale and north are HTML under the drawing (`figcaption`), so they
 * stay readable at any width; the scale bar is a percentage of the map's own
 * width. All geometry is computed once at module load with + − × ÷ only and
 * rounded (`round2`, `compactPath`), so the prerendered SVG and the first
 * client render are identical.
 *
 * Motion (`motion/useDrawPath.ts`): the dots ripple out of the base in order
 * of distance, each `g[data-map-dot] circle` popping with one
 * `[data-map-ripple]`. In the markup every dot is whole and every ripple at
 * opacity 0, so without motion the map is simply complete.
 *
 * `compact` (home): larger marks and names, no river names, no legend. Both
 * variants are capped from lg so the drawing and its caption fit the screen's
 * height.
 */

const W = FRAME.width
const H = FRAME.height
const TITLE_ID = 'vc2-map-title'
const DESC_ID = 'vc2-map-desc'
/** The base's outer ring radius, per variant scale. */
const BASE_RING_R = 10
const RASTER_WIDTHS = [1024, 1600, 2400] as const
/** Width ÷ height of the frame (4 decimals: the prerender and the client write the same). */
const ASPECT = Math.round((W / H) * 1e4) / 1e4

/** Zone page: 3xl, centred, and from lg no taller than the screen allows (drawing + caption under the header). */
const FIT_FULL = 'mx-auto max-w-3xl lg:max-w-[min(48rem,calc((100svh-12rem)*var(--map-aspect)))]'
/** Home: at the column's right edge, under the same screen-height cap. */
const FIT_COMPACT = 'mx-auto max-w-xl lg:mr-0 lg:max-w-[min(36rem,calc((100svh-12rem)*var(--map-aspect)))]'

type LonLat = readonly number[]
const at = ([lon, lat]: LonLat) => frameProject({ lon, lat })
const pathOf = (line: readonly LonLat[], closed = false) => compactPath(line.map(at), closed)

/** The map's label for a comune: «Vercellese» shortened to «V.se», as Italian maps do (the table keeps the full name). */
const mapLabel = (name: string) => name.replace(/ Vercellese$/, ' V.se')

/** Every comune of the province, projected. */
const DOTS = VC_COMUNI.map((c) => {
  const p = frameProject(c)
  return { key: c.key, name: c.name, text: mapLabel(c.name), x: round2(p.x), y: round2(p.y) }
})
const BASE_DOT = DOTS.find((d) => d.key === BASE.key)!

/**
 * Label priority: the base, then the towns a searcher knows (the plain's
 * market towns), then everyone else by distance from the base.
 */
const MAJOR = [
  'santhia', 'trino', 'crescentino', 'gattinara', 'livorno-ferraris', 'cigliano', 'borgo-vercelli',
  'arborio', 'saluggia', 'tronzano-vercellese', 'stroppiana', 'desana',
]
const dist2 = (d: { x: number; y: number }) => (d.x - BASE_DOT.x) * (d.x - BASE_DOT.x) + (d.y - BASE_DOT.y) * (d.y - BASE_DOT.y)
const PRIORITY = [
  BASE_DOT,
  ...MAJOR.map((k) => DOTS.find((d) => d.key === k)!),
  ...DOTS.filter((d) => d.key !== BASE.key && !MAJOR.includes(d.key)).sort((a, b) => dist2(a) - dist2(b) || (a.key < b.key ? -1 : 1)),
]

const RIVERS = features.rivers.map((r) => ({ name: r.name, rank: r.rank, d: r.lines.map((l) => pathOf(l)).join('') }))
const ROADS = features.roads.map((r) => ({ rank: r.rank, d: r.lines.map((l) => pathOf(l)).join('') }))

/** The point `f` of the way along a river's longest line (user units, rounded; `Math.sqrt` is correctly rounded everywhere). */
function alongRiver(name: string, f: number): { x: number; y: number } | null {
  const river = features.rivers.find((r) => r.name === name)
  if (!river) return null
  const seg = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y))
  const len = (pts: { x: number; y: number }[]) => pts.reduce((s, p, i) => (i ? s + seg(pts[i - 1], p) : 0), 0)
  const line = river.lines.map((l) => l.map(at)).reduce((a, b) => (len(b) > len(a) ? b : a))
  const target = len(line) * f
  let acc = 0
  for (let i = 1; i < line.length; i++) {
    const [a, b] = [line[i - 1], line[i]]
    const s = seg(a, b)
    if (s > 0 && acc + s >= target) {
      const t = (target - acc) / s
      return { x: round2(a.x + t * (b.x - a.x)), y: round2(a.y + t * (b.y - a.y)) }
    }
    acc += s
  }
  return null
}

/** River names along their course (`f` = how far along the drawn line), Zone page from md. */
const RIVER_FONT = 11
const RIVER_LABELS = [
  { text: 'Sesia', f: 0.72 },
  { text: 'Po', f: 0.35 },
  { text: 'Elvo', f: 0.55 },
  { text: 'Cervo', f: 0.6 },
].flatMap((r) => {
  const p = alongRiver(r.text, r.f)
  return p ? [{ text: r.text, x: p.x, y: round2(p.y - 4) }] : []
})

/** Serif italic runs a little narrower than the labels' sans. */
const serifWidth = (text: string, size: number, spacing = 0) => textWidth(text, size) * 0.9 + spacing * text.length

/** cos / sin of the landmark rotations in use (constants: `Math.cos` is not guaranteed identical across engines). */
const TRIG: Record<number, readonly [number, number]> = { 0: [1, 0], [-52]: [0.61566, -0.78801] }

/** Axis-aligned box of a (possibly rotated) text run anchored at x, y. */
function runBox(x: number, y: number, w: number, size: number, anchor: 'start' | 'middle' | 'end', rotate = 0): Box {
  const x0 = anchor === 'start' ? 0 : anchor === 'end' ? -w : -w / 2
  const corners = [
    [x0, -0.78 * size],
    [x0 + w, -0.78 * size],
    [x0, 0.24 * size],
    [x0 + w, 0.24 * size],
  ]
  const [c, s] = TRIG[rotate] ?? [1, 0]
  const xs = corners.map(([a, b]) => x + a * c - b * s)
  const ys = corners.map(([a, b]) => y + a * s + b * c)
  return { x0: Math.min(...xs) - 2, y0: Math.min(...ys) - 2, x1: Math.max(...xs) + 2, y1: Math.max(...ys) + 2 }
}

interface Variant {
  S: number
  dotR: number
  desktop: LayoutOptions
  phone: LayoutOptions
}

/** Dot radius and label sizes per variant, in user units (≈ 11 px names on desktop and on a ~21rem phone). */
const VARIANTS: Record<'full' | 'compact', Variant> = {
  full: {
    S: 1,
    dotR: 4.5,
    desktop: { width: W, height: H, fontSize: 13, dotR: 4.5, gap: 3, pad: 1.6 },
    phone: { width: W, height: H, fontSize: 25, dotR: 4.5, gap: 4, pad: 2.2 },
  },
  compact: {
    S: 1.25,
    dotR: 5.5,
    desktop: { width: W, height: H, fontSize: 16, dotR: 5.5, gap: 3.5, pad: 1.8 },
    phone: { width: W, height: H, fontSize: 25, dotR: 5.5, gap: 4, pad: 2.2 },
  },
}

const LANDMARK_SIZE = 12

/** Landmark labels at their projected anchors. */
function landmarkLabels(S: number) {
  return LANDMARKS.map((l) => {
    const p = frameProject(l)
    const x = round2(p.x)
    const y = round2(p.y)
    const size = round2(LANDMARK_SIZE * S)
    const spacing = round2(0.5 * S)
    const anchor = l.anchor ?? 'middle'
    const box = runBox(x, y, serifWidth(l.name, size, spacing), size, anchor, l.rotate)
    return { key: l.key, name: l.name, x, y, size, spacing, anchor, rotate: l.rotate, box }
  })
}

/** Everything computed per variant at module load: the fixed names, then the comuni names laid out around them. */
function build(v: Variant, withRivers: boolean) {
  const landmarks = landmarkLabels(v.S)
  const rivers = withRivers
    ? RIVER_LABELS.map((r) => ({ ...r, box: runBox(r.x, r.y, serifWidth(r.text, RIVER_FONT), RIVER_FONT, 'middle') }))
    : []
  const fixed: Box[] = [...landmarks.map((l) => l.box), ...rivers.map((r) => r.box)]
  const desktop = layoutLabels(PRIORITY, DOTS, { ...v.desktop, obstacles: fixed })
  const phone = layoutLabels(PRIORITY, DOTS, v.phone)
  return { landmarks, rivers, desktop, phone }
}

const LAYOUT = { full: build(VARIANTS.full, true), compact: build(VARIANTS.compact, false) }

/** 10 km as a share of the map's width, for the HTML scale bar. */
const KM10_PCT = round2((10 / frameKmPerUnit() / W) * 100)

/** Label halo: a paper stroke painted under the glyphs. */
const HALO = { className: 'stroke-vc2-paper', strokeLinejoin: 'round' as const, paintOrder: 'stroke' }
/** vc2's serif (the `.vc2-display` face) without that class's weight and tracking. */
const SERIF = 'font-[Georgia,serif]'
const WATER_TEXT = 'fill-[#4f7d96] dark:fill-[#a9c7d8]'
const WATER_LINE = 'stroke-[#7ea7bf] dark:stroke-[#5f8ca3]'
/** Legend swatches, keyed to the raster's tones (render-territory.mjs THEMES). */
const PADDY_SWATCH = 'fill-[#d2dfae] stroke-[#c9d4ab] dark:fill-[#1a3125] dark:stroke-[#4f6b58]'
const PADDY_BANK = 'stroke-[#eef2e0] dark:stroke-[#35553f]'
const WOOD_SWATCH = 'fill-[#a9c295] stroke-[#95b081] dark:fill-[#0a1f14] dark:stroke-[#4f6b58]'
const BORDER_LINE = 'stroke-[#5d7d5a] dark:stroke-[#9fbf9f]'
/** The service colour: dots and the base ring (forest on paper, brass-bright in dark). */
const ACCENT_STROKE = 'stroke-vc2-accent'
const DOT = 'fill-vc2-accent stroke-vc2-paper'

function Territory({ theme, sizes, className }: { theme: 'light' | 'dark'; sizes: string; className: string }) {
  const role = `territory-${theme}`
  return (
    <picture aria-hidden="true" className={`absolute inset-0 ${className}`}>
      <source type="image/avif" srcSet={srcSetFor(role, 'avif', RASTER_WIDTHS)} sizes={sizes} />
      <source type="image/webp" srcSet={srcSetFor(role, 'webp', RASTER_WIDTHS)} sizes={sizes} />
      <img src={srcFor(role, 1600, 'webp')} alt="" width={W} height={H} loading="lazy" decoding="async" className="h-full w-full" />
    </picture>
  )
}

function Names({ labels, fontSize, className }: { labels: readonly PlacedLabel[]; fontSize: number; className: string }) {
  const text = new Map(DOTS.map((d) => [d.key, d.text]))
  return (
    <g className={className} aria-hidden="true">
      {labels.map((l) => (
        <text
          key={l.key}
          x={l.x}
          y={l.y}
          textAnchor={l.anchor}
          className={`font-sans ${l.key === BASE.key ? 'font-bold' : 'font-semibold'} fill-vc2-ink ${HALO.className}`}
          fontSize={l.key === BASE.key ? round2(fontSize * 1.15) : fontSize}
          strokeWidth={round2(fontSize * 0.28)}
          strokeLinejoin={HALO.strokeLinejoin}
          paintOrder={HALO.paintOrder}
        >
          {text.get(l.key)}
        </text>
      ))}
    </g>
  )
}

export function ZoneMap({ compact = false }: { compact?: boolean }) {
  const copy = useCopy()
  const m = copy.zone.map
  const v = VARIANTS[compact ? 'compact' : 'full']
  const L = LAYOUT[compact ? 'compact' : 'full']
  const u = (n: number) => round2(n * v.S)
  const sizes = compact ? '(min-width: 640px) 36rem, 100vw' : '(min-width: 640px) 48rem, 100vw'

  return (
    <figure className={compact ? FIT_COMPACT : FIT_FULL} style={{ '--map-aspect': ASPECT } as CSSProperties}>
      <div className="relative overflow-hidden rounded-sm bg-vc2-paper" style={{ aspectRatio: `${W} / ${H}` }}>
        <Territory theme="light" sizes={sizes} className="dark:hidden" />
        <Territory theme="dark" sizes={sizes} className="hidden dark:block" />
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-labelledby={TITLE_ID}
          aria-describedby={DESC_ID}
          className="absolute inset-0 block h-full w-full"
        >
          <title id={TITLE_ID}>{copy.zone.title}</title>
          <desc id={DESC_ID}>{m.desc}</desc>

          {/* Water */}
          <g fill="none" strokeLinecap="round" strokeLinejoin="round" className={WATER_LINE}>
            {RIVERS.map((r) => (
              <path key={r.name} d={r.d} strokeWidth={u(r.rank === 1 ? 1.8 : 1.1)} />
            ))}
          </g>

          {/* Roads: a paper casing under an ink core */}
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            {ROADS.map((r) => (
              <g key={r.rank}>
                <path d={r.d} className="stroke-vc2-paper" strokeOpacity={0.5} strokeWidth={u(r.rank === 1 ? 2.2 : 1.6)} />
                <path d={r.d} className="stroke-vc2-ink-soft" strokeOpacity={0.4} strokeWidth={u(r.rank === 1 ? 0.8 : 0.5)} />
              </g>
            ))}
          </g>

          {/* The territory's names, from md (the <desc> names them for assistive tech) */}
          <g data-map-landmarks aria-hidden="true" className={`max-md:hidden ${SERIF} italic fill-vc2-ink-soft ${HALO.className}`}>
            {L.landmarks.map((l) => (
              <text
                key={l.key}
                x={l.x}
                y={l.y}
                textAnchor={l.anchor}
                transform={l.rotate ? `rotate(${l.rotate} ${l.x} ${l.y})` : undefined}
                fontSize={l.size}
                letterSpacing={l.spacing}
                strokeOpacity={0.75}
                strokeWidth={round2(l.size * 0.28)}
                strokeLinejoin={HALO.strokeLinejoin}
                paintOrder={HALO.paintOrder}
              >
                {l.name}
              </text>
            ))}
          </g>

          {/* River names (Zone page, from md) */}
          {L.rivers.length > 0 && (
            <g aria-hidden="true" className={`max-md:hidden ${SERIF} italic ${WATER_TEXT} ${HALO.className}`}>
              {L.rivers.map((r) => (
                <text key={r.text} x={r.x} y={r.y} textAnchor="middle" fontSize={RIVER_FONT} strokeOpacity={0.7} strokeWidth={2.6} strokeLinejoin={HALO.strokeLinejoin} paintOrder={HALO.paintOrder}>
                  {r.text}
                </text>
              ))}
            </g>
          )}

          {/* Comuni: one dot each (name in its <title>), the base with its ring */}
          <circle
            cx={BASE_DOT.x}
            cy={BASE_DOT.y}
            r={u(BASE_RING_R)}
            className={`fill-none ${ACCENT_STROKE} [transform-box:fill-box] [transform-origin:center] motion-safe:animate-[ping_2.6s_cubic-bezier(0,0,0.2,1)_infinite]`}
            strokeWidth={u(1.2)}
          />
          {DOTS.map((d) => {
            const isBase = d.key === BASE.key
            const r = isBase ? round2(v.dotR * 1.5) : v.dotR
            return (
              <g key={d.key}>
                <circle data-map-ripple cx={d.x} cy={d.y} r={r} fill="none" className={WATER_LINE} strokeWidth={u(1.2)} opacity={0} />
                <g data-map-dot transform={`translate(${d.x} ${d.y})`}>
                  <title>{d.name}</title>
                  <circle r={r} className={DOT} strokeWidth={u(1)} />
                  {isBase && <circle r={u(BASE_RING_R)} className={`fill-none ${ACCENT_STROKE}`} strokeWidth={u(1.2)} />}
                </g>
              </g>
            )
          })}

          {/* Comuni names: desktop set and phone set */}
          <Names labels={L.desktop} fontSize={v.desktop.fontSize} className="max-md:hidden" />
          <Names labels={L.phone} fontSize={v.phone.fontSize} className="md:hidden" />

          {/* Neatline */}
          <rect x={0.5} y={0.5} width={W - 1} height={H - 1} fill="none" className="stroke-vc2-ink/25" />
        </svg>
      </div>

      <figcaption className="mt-4 space-y-3 text-xs text-vc2-ink-soft">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <span className="vc2-kicker flex items-center gap-3 text-sm">
            <svg aria-hidden="true" viewBox="0 0 28 28" className="h-4 w-4 shrink-0">
              <circle cx="14" cy="14" r="7" className="fill-vc2-accent" />
              <circle cx="14" cy="14" r="12" className="fill-none stroke-vc2-accent" strokeWidth="1.5" />
            </svg>
            <span>
              {copy.zone.baseLabel} · {BASE.name}
            </span>
          </span>
        </div>
        {/* Scale: the bar is 10 km's share of the map's width (a flex item's % resolves against this row, as wide as the map); then north */}
        <div className="flex items-center gap-2" aria-hidden="true">
          <span
            className="block h-1.5 shrink-0 border border-vc2-ink bg-[linear-gradient(90deg,rgb(var(--vc2-ink))_50%,transparent_50%)]"
            style={{ width: `${KM10_PCT}%` }}
          />
          <span className="tabular-nums">10 km</span>
          <span className="ml-auto font-semibold text-vc2-ink">N ↑</span>
        </div>
        {!compact && (
          <ul className="flex flex-wrap gap-x-5 gap-y-2" aria-label={m.legendTitle}>
            <li className="flex items-center gap-2">
              <svg aria-hidden="true" viewBox="0 0 12 12" className="h-3 w-3"><circle cx="6" cy="6" r="4" className={DOT} strokeWidth="1.2" /></svg>
              {m.legend.comune}
            </li>
            <li className="flex items-center gap-2">
              <svg aria-hidden="true" viewBox="0 0 22 10" className="h-2.5 w-6"><path d="M0 6C4 2 7 10 11 6S18 2 22 6" fill="none" className={WATER_LINE} strokeWidth="1.8" /></svg>
              {m.legend.water}
            </li>
            <li className="flex items-center gap-2">
              <svg aria-hidden="true" viewBox="0 0 22 10" className="h-2.5 w-6"><rect width="22" height="10" rx="1.5" className={PADDY_SWATCH} strokeWidth="0.8" /><path d="M11 0V10" className={PADDY_BANK} strokeWidth="1" /></svg>
              {m.legend.paddy}
            </li>
            <li className="flex items-center gap-2">
              <svg aria-hidden="true" viewBox="0 0 22 10" className="h-2.5 w-6"><rect width="22" height="10" rx="1.5" className={WOOD_SWATCH} strokeWidth="0.8" /></svg>
              {m.legend.wood}
            </li>
            <li className="flex items-center gap-2">
              <svg aria-hidden="true" viewBox="0 0 22 10" className="h-2.5 w-6"><path d="M0 5H22" fill="none" className={BORDER_LINE} strokeWidth="1.6" strokeDasharray="4 2.5" /></svg>
              {m.legend.province}
            </li>
          </ul>
        )}
        <p>
          {m.reliefCredit} ·{' '}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-vc2-hairline underline-offset-2 hover:decoration-current"
          >
            {m.osmCredit}
          </a>
        </p>
      </figcaption>
    </figure>
  )
}
